import { useEffect, useRef, useState } from 'react';
import { PutObjectCommand } from '@aws-sdk/client-s3';
import { config } from './config';
import { getS3 } from './aws';
import { useAuth } from './auth';
import { useHlsSource } from './hls-player';
import { buildThumbnailKey } from './uploads';

/**
 * Lets a user scrub through a video and set the exact frame under the scrubber as its thumbnail.
 * Owns its own `<video>` element (muted, not the shared `HlsPlayer` component) so it can freely
 * seek without fighting whatever playback state the visible player is in — see design.md.
 *
 * Reused for both personal uploads and shared-library videos — the capture-and-upload-to-S3 logic
 * is identical either way; only how the resulting URL gets persisted differs, so that's left to
 * the caller via `onSave` rather than this component calling a specific API function itself.
 */
export function ThumbnailPicker({
  subjectId,
  hlsUrl,
  onSave,
  onCancel,
}: {
  /** Used only to build the uploaded image's S3 key — an uploadId or a shared videoId work
   * equally well here, since it's just a path segment, not a lookup key. */
  subjectId: string;
  hlsUrl: string;
  /** Persists the captured thumbnail (e.g. calling `setThumbnail` or `setSharedThumbnail`) and
   * handles any resulting UI transition (closing the picker, refetching) — on success, this
   * component doesn't need to do anything further itself. */
  onSave: (thumbnailUrl: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { identityId } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [slowLoad, setSlowLoad] = useState(false);

  useHlsSource(videoRef, hlsUrl, false, setLoadError, true, true);

  // Some load failures (e.g. a manifest that never resolves) don't fire any error event at all —
  // without this, the picker would just sit disabled forever with no explanation.
  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(() => setSlowLoad(true), 8000);
    return () => clearTimeout(timer);
  }, [ready, hlsUrl]);

  const drawFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  };

  const onLoadedMetadata = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    setDuration(video.duration);
    setReady(true);
    video.currentTime = 0;
  };

  const onSeeked = () => {
    drawFrame();
    setCurrentTime(videoRef.current?.currentTime ?? 0);
  };

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const confirm = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !identityId) return;
    setSaving(true);
    setError(null);
    try {
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.85),
      );
      if (!blob) throw new Error('Could not capture frame.');
      const key = buildThumbnailKey(identityId, subjectId);
      await getS3().send(
        new PutObjectCommand({
          Bucket: config.thumbnailsBucket,
          Key: key,
          Body: blob,
          ContentType: 'image/jpeg',
        }),
      );
      const thumbnailUrl = `https://${config.spaDomain}/${key}`;
      await onSave(thumbnailUrl);
    } catch {
      setError("Couldn't set thumbnail — try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Choose a thumbnail</h2>
      <p className="muted">Drag the slider to find the frame you want, then confirm.</p>
      {/* Kept out of view but not `display: none` — some browsers skip decoding entirely for a
          display:none video, which would silently break every capture below. */}
      <video
        ref={videoRef}
        crossOrigin="anonymous"
        muted
        playsInline
        onLoadedMetadata={onLoadedMetadata}
        onSeeked={onSeeked}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', maxHeight: '50vh', objectFit: 'contain', background: 'black' }}
      />
      {loadError ? (
        <p className="error">{loadError}</p>
      ) : (
        slowLoad &&
        !ready && (
          <p className="muted">Still loading the video… this is taking longer than expected.</p>
        )
      )}
      <input
        type="range"
        min={0}
        max={duration}
        step="any"
        value={currentTime}
        onChange={onScrub}
        disabled={!ready}
        style={{ width: '100%', marginTop: 'var(--space-3)' }}
      />
      <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 8 }}>
        <button onClick={confirm} disabled={!ready || saving}>
          {saving ? 'Saving…' : 'Set as thumbnail'}
        </button>
        <button type="button" className="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
}
